import React from "react";
import { Link } from "react-router-dom";
import { FileBarChart2, Copy, ArrowUpRight, Info } from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { useCopies } from "../hooks/useCopies";

const formatMoney = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Reports = () => {
  const { copies, totalAllocated, totalValue, totalPnL, totalReturnPct } = useCopies();
  const hasActivity = copies.length > 0 || totalAllocated > 0;

  if (!hasActivity) {
    return (
      <DashboardLayout title="Reports" subtitle="Performance analytics" activeKey="reports">
        <div className="bb-card rounded-2xl p-8 lg:p-12 text-center" data-testid="reports-empty">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-white/[0.04] border border-white/8 flex items-center justify-center">
            <FileBarChart2 size={22} className="text-white/55" />
          </div>
          <div className="font-display font-bold text-[20px] text-white mb-2">
            Nothing to report yet
          </div>
          <p className="text-[13px] text-white/55 max-w-md mx-auto leading-relaxed mb-6">
            Win rate, profit factor, P&amp;L by symbol, and your equity curve will appear here once you have account activity. Start by copying a trader.
          </p>
          <Link
            to="/dashboard/copy-trading"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bb-blue text-white text-[13px] font-medium rounded-lg"
            data-testid="reports-copy-cta"
          >
            <Copy size={14} /> Browse traders <ArrowUpRight size={14} />
          </Link>

          <div className="mt-8 max-w-md mx-auto flex items-start gap-2.5 px-4 py-3 rounded-lg bg-white/[0.02] border border-white/8 text-left">
            <Info size={14} className="text-[#7d8eff] shrink-0 mt-0.5" />
            <div className="text-[11px] text-white/55 leading-relaxed">
              Detailed manual-trade analytics arrive when the live trade engine ships.
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Live copy-trading metrics
  return (
    <DashboardLayout title="Reports" subtitle="Performance analytics" activeKey="reports">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="bb-card rounded-2xl p-5">
          <div className="text-[11px] text-white/55 mb-1">Total allocated</div>
          <div className="font-display text-[24px] font-bold tabular-nums">
            ${formatMoney(totalAllocated)}
          </div>
        </div>
        <div className="bb-card rounded-2xl p-5">
          <div className="text-[11px] text-white/55 mb-1">Current value</div>
          <div className="font-display text-[24px] font-bold tabular-nums">
            ${formatMoney(totalValue)}
          </div>
        </div>
        <div className="bb-card rounded-2xl p-5">
          <div className="text-[11px] text-white/55 mb-1">Net P&amp;L</div>
          <div
            className={`font-display text-[24px] font-bold tabular-nums ${
              totalPnL >= 0 ? "text-emerald-400" : "text-rose-400"
            }`}
          >
            {totalPnL >= 0 ? "+" : ""}${formatMoney(totalPnL)}
          </div>
        </div>
        <div className="bb-card rounded-2xl p-5">
          <div className="text-[11px] text-white/55 mb-1">Return</div>
          <div
            className={`font-display text-[24px] font-bold tabular-nums ${
              totalReturnPct >= 0 ? "text-emerald-400" : "text-rose-400"
            }`}
          >
            {totalReturnPct >= 0 ? "+" : ""}
            {totalReturnPct.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="bb-card rounded-2xl p-5 lg:p-6">
        <div className="font-display font-semibold text-[15px] mb-5">P&amp;L by trader</div>
        <div className="space-y-3">
          {copies.map((c) => {
            const pnl = c.currentValue - c.allocated;
            const ret = c.allocated > 0 ? (pnl / c.allocated) * 100 : 0;
            const positive = pnl >= 0;
            return (
              <div key={c.id} className="grid grid-cols-[140px_1fr_120px] items-center gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                    style={{ background: c.traderColor }}
                  >
                    {c.traderAvatar}
                  </div>
                  <div className="text-[13px] text-white font-medium truncate">{c.traderName}</div>
                </div>
                <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                  <div
                    className={`h-full rounded-full ${positive ? "bg-emerald-500" : "bg-rose-500"}`}
                    style={{ width: `${Math.max(2, Math.min(100, Math.abs(ret) * 5))}%` }}
                  ></div>
                </div>
                <div
                  className={`text-right text-[13px] tabular-nums font-medium ${
                    positive ? "text-emerald-400" : "text-rose-400"
                  }`}
                >
                  {positive ? "+" : ""}${formatMoney(pnl)} ({positive ? "+" : ""}
                  {ret.toFixed(2)}%)
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
